import { PageLayout } from "@/v2/components/layout";
import { Flex, Text, Box } from "@radix-ui/themes";
import { FileTextIcon } from "@radix-ui/react-icons";
import { useMemo } from "react";
import styles from "./CustomReportEmptyState.module.scss";

type NextReportDue = {
  nextRun: string;
};

interface CustomReportEmptyStateProps {
  nextReportDue?: NextReportDue;
  is404?: boolean;
}

export function CustomReportEmptyState({
  nextReportDue,
  is404 = false,
}: CustomReportEmptyStateProps) {
  const title = useMemo(() => {
    return is404 ? "Failed to find report" : "No Reports Yet";
  }, [is404]);

  const description = useMemo(() => {
    if (nextReportDue?.nextRun) {
      const nextRunDate = new Date(nextReportDue.nextRun);
      const formattedDate = nextRunDate.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      return `The first report will be generated on ${formattedDate}.`;
    }
    if (is404) {
      return "Please check the URL or try again.";
    }
    return "Please wait for the first report to be generated.";
  }, [nextReportDue, is404]);

  return (
    <PageLayout variant="v2">
      <Flex
        align="center"
        justify="center"
        direction="column"
        className={styles.container}
      >
        <Box className={styles.iconContainer}>
          <FileTextIcon width={64} height={64} />
        </Box>
        <Flex
          direction="column"
          gap="3"
          align="center"
          className={styles.content}
        >
          <Text size="6" weight="medium" color="gray" highContrast>
            {title}
          </Text>
          <Text
            size="3"
            color="gray"
            align="center"
            className={styles.description}
          >
            {description}
          </Text>
        </Flex>
      </Flex>
    </PageLayout>
  );
}
